import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import KnowledgeBase from './KnowledgeBase';
import ClientSearch from '../components/ClientSearch';

function formatTime(ts) {
  const d = new Date(ts);
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function FAQ() {
  const { authFetch, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('faq');
  // Auto-mail tab state
  const [autoEmailEnabled, setAutoEmailEnabled] = useState(null); // null = loading
  const [autoEmailDrafts, setAutoEmailDrafts] = useState([]);
  const [autoEmailLoading, setAutoEmailLoading] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [draftSubject, setDraftSubject] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [draftToEmail, setDraftToEmail] = useState('');
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftSendResult, setDraftSendResult] = useState(null);
  const [runningCheck, setRunningCheck] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [context, setContext] = useState('');
  const [contextFiles, setContextFiles] = useState([]); // { name, text }
  const [contextUrls, setContextUrls] = useState('');
  const [extractingContext, setExtractingContext] = useState(false);
  const contextFileRef = useRef(null);
  // Bookings
  const [bookings, setBookings] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [showHtmlPreview, setShowHtmlPreview] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null); // { ok, message }
  // Client
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    authFetch('/api/faq/inquiries')
      .then(r => r.json())
      .then(data => { setInquiries(Array.isArray(data) ? data : (data?.inquiries || [])); setLoading(false); })
      .catch(() => setLoading(false));
    authFetch('/api/bookings')
      .then(r => r.ok ? r.json() : [])
      .then(data => setBookings(Array.isArray(data) ? data : []))
      .catch(() => {});
    authFetch('/api/settings')
      .then(r => r.ok ? r.json() : {})
      .then(data => setAutoEmailEnabled(!!data.auto_email_enabled))
      .catch(() => setAutoEmailEnabled(false));
  }, []);

  const loadAutoEmailDrafts = async () => {
    setAutoEmailLoading(true);
    try {
      const res = await authFetch('/api/auto-emails');
      const data = await res.json();
      setAutoEmailDrafts(Array.isArray(data) ? data : []);
    } catch (_) {}
    setAutoEmailLoading(false);
  };

  const handleSelectDraft = (draft) => {
    setSelectedDraft(draft);
    setDraftSubject(draft.subject || '');
    setDraftBody(draft.body_html || '');
    setDraftToEmail(draft.recipient_email || '');
    setDraftSendResult(null);
  };

  const saveDraftEdits = async () => {
    if (!selectedDraft) return;
    setDraftSaving(true);
    try {
      const res = await authFetch(`/api/auto-emails/${selectedDraft.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ subject: draftSubject, body_html: draftBody }),
      });
      const updated = await res.json();
      setAutoEmailDrafts(prev => prev.map(d => d.id === updated.id ? { ...d, ...updated } : d));
      setSelectedDraft(prev => ({ ...prev, ...updated }));
    } catch (_) {}
    setDraftSaving(false);
  };

  const skipDraft = async (id) => {
    try {
      await authFetch(`/api/auto-emails/${id}/skip`, { method: 'POST' });
      setAutoEmailDrafts(prev => prev.map(d => d.id === id ? { ...d, status: 'skipped' } : d));
      if (selectedDraft?.id === id) setSelectedDraft(null);
    } catch (_) {}
  };

  const sendDraft = async () => {
    if (!selectedDraft) return;
    setDraftSendResult(null);
    setDraftSaving(true);
    try {
      const res = await authFetch(`/api/auto-emails/${selectedDraft.id}/send`, {
        method: 'POST',
        body: JSON.stringify({ to_email: draftToEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Versturen mislukt');
      setDraftSendResult({ ok: true, message: `Verstuurd naar ${data.sentTo}` });
      setAutoEmailDrafts(prev => prev.map(d => d.id === selectedDraft.id ? { ...d, status: 'sent' } : d));
      setSelectedDraft(prev => ({ ...prev, status: 'sent' }));
      setTimeout(() => setDraftSendResult(null), 6000);
    } catch (err) {
      setDraftSendResult({ ok: false, message: err.message });
    }
    setDraftSaving(false);
  };

  const runAutoCheck = async () => {
    setRunningCheck(true);
    try {
      await authFetch('/api/auto-emails/run-check', { method: 'POST' });
      await loadAutoEmailDrafts();
    } catch (_) {}
    setRunningCheck(false);
  };

  const handleBookingSelect = (bookingId) => {
    setSelectedBookingId(bookingId);
    if (!bookingId) return;
    const b = bookings.find(x => String(x.id) === String(bookingId));
    if (!b) return;
    const names = Array.isArray(b.traveler_names) ? b.traveler_names.join(' & ') : (b.traveler_names || 'Onbekend');
    const dep = b.departure_date ? new Date(b.departure_date).toLocaleDateString('nl-NL') : '?';
    const ret = b.return_date ? new Date(b.return_date).toLocaleDateString('nl-NL') : '?';
    const bookingText = [
      `--- BOEKING GESELECTEERD ---`,
      `Reizigers: ${names}`,
      `Bestemming: ${b.destination || '?'}`,
      `Periode: ${dep} – ${ret} (${b.duration_days || '?'} dagen)`,
      `Vlucht heen: ${b.outbound_flight || '?'} | vertrek ${b.outbound_departure_time || '?'} | aankomst ${b.outbound_arrival_time || '?'}`,
      b.return_flight ? `Vlucht terug: ${b.return_flight} | vertrek ${b.return_departure_time || '?'} | aankomst ${b.return_arrival_time || '?'}` : null,
      `Hotel: ${b.hotel_name || '?'}${b.hotel_location ? ', ' + b.hotel_location : ''}`,
      `Verzorging: ${b.board_type || '?'} | Kamer: ${b.room_type || '?'}`,
      `Reisorganisatie: ${b.travel_organization || '?'}`,
      `Totaalprijs: €${b.total_price || '?'}`,
      `---`,
    ].filter(Boolean).join('\n');
    setContext(bookingText);
  };

  const handleContextFile = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      const fd = new FormData();
      fd.append('pdf', file);
      const token = localStorage.getItem('tripdash_token');
      try {
        const res = await fetch('/api/newsletter/extract-pdf', {
          method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
        });
        const data = await res.json();
        if (data.text) setContextFiles(prev => [...prev, { name: file.name, text: data.text }]);
      } catch(e) {}
    }
    e.target.value = '';
  };

  const generateReply = async () => {
    if (!selected) return;
    setGenerating(true);
    setReply('');
    try {
      let additional_context = '';
      if (context.trim()) additional_context += `Agent context:\n${context.trim()}\n\n`;
      if (contextFiles.length) additional_context += contextFiles.map(f => `Bestand "${f.name}":\n${f.text}`).join('\n\n');
      if (contextUrls.trim()) additional_context += `\nURL's voor context: ${contextUrls}`;

      const res = await authFetch('/api/faq/generate-reply', {
        method: 'POST',
        body: JSON.stringify({
          inquiry_id: selected.id,
          inquiry_text: selected.full_text,
          additional_context: additional_context.trim(),
          client_id: selectedClient?.client_id ?? null,
        }),
      });
      const data = await res.json();
      setReply(data.reply || '');
    } catch (err) {
      setReply('Fout bij genereren van antwoord.');
    } finally {
      setGenerating(false);
    }
  };

  const markReplied = async () => {
    if (!selected) return;
    await authFetch(`/api/faq/mark-replied/${selected.id}`, { method: 'POST' });
    setInquiries(prev => prev.map(i => i.id === selected.id ? { ...i, status: 'Replied' } : i));
    setSelected(prev => ({ ...prev, status: 'Replied' }));
  };

  const syncImap = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const res = await authFetch('/api/faq/sync-imap', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSyncMsg(data.message);
      const r = await authFetch('/api/faq/inquiries');
      const fresh = await r.json();
      setInquiries(Array.isArray(fresh) ? fresh : (fresh?.inquiries || []));
      setTimeout(() => setSyncMsg(''), 5000);
    } catch (err) {
      setSyncMsg('Fout: ' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteInquiry = async (e, id) => {
    e.stopPropagation();
    setDeletingId(id);
    try {
      await authFetch(`/api/faq/inquiries/${id}`, { method: 'DELETE' });
      setInquiries(prev => prev.filter(i => i.id !== id));
      if (selected?.id === id) { setSelected(null); setReply(''); }
    } catch (_) {}
    setDeletingId(null);
  };

  const sendReply = async () => {
    if (!selected || !reply) return;
    setSending(true);
    setSendResult(null);
    try {
      const res = await authFetch('/api/faq/send-reply', {
        method: 'POST',
        body: JSON.stringify({
          inquiry_id: selected.id,
          body_html: reply,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Onbekende fout');
      setSendResult({ ok: true, message: `Verstuurd naar ${data.sentTo}` });
      setInquiries(prev => prev.map(i => i.id === selected.id ? { ...i, status: 'Replied' } : i));
      setSelected(prev => ({ ...prev, status: 'Replied' }));
      setTimeout(() => setSendResult(null), 6000);
    } catch (err) {
      setSendResult({ ok: false, message: err.message });
    } finally {
      setSending(false);
    }
  };

  const copyReply = () => {
    try {
      const blob = new Blob([reply], { type: 'text/html' });
      const item = new ClipboardItem({ 'text/html': blob });
      navigator.clipboard.write([item]).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    } catch {
      const plain = reply.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
      navigator.clipboard.writeText(plain).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Page header */}
      <div className="pro-page-header flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">FAQ</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {activeTab === 'faq' ? 'Genereer AI-antwoorden op klantvragen' : activeTab === 'knowledge' ? 'FAQ\'s en beleid per leverancier' : 'Automatisch gegenereerde klant-e-mails'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {activeTab === 'faq' && syncMsg && (
            <span className={`text-sm ${syncMsg.startsWith('Fout') ? 'text-red-600' : 'text-green-600'}`}>
              {syncMsg}
            </span>
          )}
          {activeTab === 'faq' && (
            <button onClick={syncImap} disabled={syncing} className="btn-secondary text-sm">
              {syncing ? <span className="spinner spinner-blue" /> : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
              {syncing ? 'Synchroniseren...' : 'Sync e-mail'}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-gray-200 px-6 flex-shrink-0">
        <button
          onClick={() => setActiveTab('faq')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'faq' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Auto-Reply
        </button>
        {isAdmin && (
          <button
            onClick={() => setActiveTab('knowledge')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'knowledge' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            Leverancierskennis
          </button>
        )}
        <button
          onClick={() => { setActiveTab('auto-mail'); if (autoEmailEnabled) loadAutoEmailDrafts(); }}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'auto-mail' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Automatische mail
          {autoEmailDrafts.filter(d => d.status === 'pending').length > 0 && (
            <span className="ml-1.5 bg-blue-600 text-white text-xs rounded-full px-1.5 py-0.5 leading-none">
              {autoEmailDrafts.filter(d => d.status === 'pending').length}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'knowledge' ? <KnowledgeBase /> : null}

      {activeTab === 'auto-mail' && (
        <div className="flex flex-1 overflow-hidden">
          {/* Feature disabled */}
          {autoEmailEnabled === false && (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center max-w-sm">
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1">Automatische e-mails zijn uitgeschakeld</h3>
                <p className="text-xs text-gray-400 leading-relaxed">
                  Schakel deze functie in via <strong>Instellingen → Voorkeuren → Automatisering</strong>.
                  Zodra ingeschakeld genereert het systeem dagelijks concepten voor pre-trip en post-trip e-mails.
                </p>
              </div>
            </div>
          )}

          {/* Feature enabled */}
          {autoEmailEnabled === true && (
            <>
              {/* Left: draft list */}
              <div className="w-80 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col overflow-hidden">
                <div className="p-3 border-b border-gray-100 flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Concepten</span>
                  <button
                    onClick={runAutoCheck}
                    disabled={runningCheck}
                    className="btn-secondary text-xs py-1 px-2.5 flex items-center gap-1.5"
                    title="Controleer nu op nieuwe concepten"
                  >
                    {runningCheck ? <span className="spinner spinner-blue w-3 h-3" /> : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    )}
                    {runningCheck ? 'Bezig...' : 'Controleer nu'}
                  </button>
                </div>
                <div className="overflow-y-auto flex-1">
                  {autoEmailLoading ? (
                    <div className="flex items-center justify-center h-24">
                      <div className="spinner spinner-blue" />
                    </div>
                  ) : autoEmailDrafts.length === 0 ? (
                    <div className="p-6 text-center text-gray-400 text-xs">
                      Geen concepten gevonden.<br />Klik op "Controleer nu" om te genereren.
                    </div>
                  ) : (
                    autoEmailDrafts.map(draft => {
                      const booking = draft.bookings;
                      const statusColors = {
                        pending:  'bg-amber-100 text-amber-700',
                        sent:     'bg-green-100 text-green-700',
                        skipped:  'bg-gray-100 text-gray-500',
                        approved: 'bg-blue-100 text-blue-700',
                      };
                      const statusLabels = { pending: 'Concept', sent: 'Verstuurd', skipped: 'Overgeslagen', approved: 'Goedgekeurd' };
                      return (
                        <div
                          key={draft.id}
                          onClick={() => handleSelectDraft(draft)}
                          className={`border-b border-gray-100 p-3.5 cursor-pointer hover:bg-gray-50 transition-colors ${selectedDraft?.id === draft.id ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''}`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${statusColors[draft.status] || statusColors.pending}`}>
                              {statusLabels[draft.status] || draft.status}
                            </span>
                            <span className="text-xs text-gray-400 capitalize">
                              {draft.automation_type === 'pre_trip' ? 'Pre-trip' : 'Post-trip'}
                            </span>
                          </div>
                          <p className="text-xs font-semibold text-gray-800 truncate mt-1">{draft.subject}</p>
                          {booking && (
                            <p className="text-xs text-gray-400 mt-0.5 truncate">
                              {booking.destination || '?'} · {booking.departure_date ? new Date(booking.departure_date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : ''}
                            </p>
                          )}
                          {draft.scheduled_for && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              Gepland: {new Date(draft.scheduled_for).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                            </p>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Right: draft detail */}
              <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
                {!selectedDraft ? (
                  <div className="flex items-center justify-center h-full text-center">
                    <div>
                      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center mx-auto mb-3">
                        <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-gray-600">Selecteer een concept</p>
                      <p className="text-xs text-gray-400 mt-1">Klik op een concept links om het te bekijken</p>
                    </div>
                  </div>
                ) : (
                  <div className="max-w-2xl">
                    <div className="flex items-center gap-3 mb-5">
                      <span className={`text-xs px-2 py-1 rounded font-medium ${selectedDraft.automation_type === 'pre_trip' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                        {selectedDraft.automation_type === 'pre_trip' ? 'Pre-trip' : 'Post-trip'}
                      </span>
                      {selectedDraft.status === 'sent' && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-medium">Verstuurd</span>
                      )}
                      {selectedDraft.status === 'skipped' && (
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded font-medium">Overgeslagen</span>
                      )}
                    </div>

                    {/* Booking context */}
                    {selectedDraft.bookings && (
                      <div className="bg-white rounded-lg border border-gray-100 p-3.5 mb-4 text-xs text-gray-600">
                        <p className="font-semibold text-gray-700 mb-1">Boeking</p>
                        <p>{selectedDraft.bookings.destination || '?'} · {selectedDraft.bookings.departure_date ? new Date(selectedDraft.bookings.departure_date).toLocaleDateString('nl-NL') : ''} – {selectedDraft.bookings.return_date ? new Date(selectedDraft.bookings.return_date).toLocaleDateString('nl-NL') : ''}</p>
                        {selectedDraft.bookings.hotel_name && <p>{selectedDraft.bookings.hotel_name}</p>}
                      </div>
                    )}

                    {/* Subject */}
                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Onderwerp</label>
                      <input
                        className="input text-sm"
                        value={draftSubject}
                        onChange={e => setDraftSubject(e.target.value)}
                        disabled={selectedDraft.status === 'sent'}
                      />
                    </div>

                    {/* Body */}
                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">E-mailtekst</label>
                      <textarea
                        className="input text-sm leading-relaxed"
                        style={{ minHeight: 200 }}
                        value={draftBody}
                        onChange={e => setDraftBody(e.target.value)}
                        disabled={selectedDraft.status === 'sent'}
                      />
                    </div>

                    {/* Recipient */}
                    <div className="mb-4">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Ontvanger e-mailadres</label>
                      <input
                        className="input text-sm"
                        type="email"
                        placeholder="klant@voorbeeld.nl"
                        value={draftToEmail}
                        onChange={e => setDraftToEmail(e.target.value)}
                        disabled={selectedDraft.status === 'sent'}
                      />
                    </div>

                    {draftSendResult && (
                      <div className={`mb-4 px-3 py-2.5 rounded-lg text-sm border ${draftSendResult.ok ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                        {draftSendResult.message}
                      </div>
                    )}

                    {selectedDraft.status !== 'sent' && selectedDraft.status !== 'skipped' && (
                      <div className="flex gap-2 flex-wrap pt-3 border-t border-gray-100">
                        <button
                          onClick={saveDraftEdits}
                          disabled={draftSaving}
                          className="btn-secondary text-sm"
                        >
                          {draftSaving ? <span className="spinner spinner-blue" /> : null}
                          Wijzigingen opslaan
                        </button>
                        <button
                          onClick={sendDraft}
                          disabled={draftSaving || !draftToEmail}
                          className="btn-primary text-sm"
                        >
                          {draftSaving ? <span className="spinner" /> : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                          )}
                          Verstuur e-mail
                        </button>
                        <button
                          onClick={() => skipDraft(selectedDraft.id)}
                          className="btn-danger text-sm"
                        >
                          Overslaan
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'faq' && <div className="flex flex-1 overflow-hidden">
        {/* Left: inquiry list */}
        <div className="w-72 flex-shrink-0 border-r border-gray-200 bg-white overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="spinner spinner-blue" />
            </div>
          ) : inquiries.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">Geen vragen gevonden</div>
          ) : (
            inquiries.map(inq => (
              <div
                key={inq.id}
                className={`relative group border-b border-gray-100 ${selected?.id === inq.id ? 'bg-blue-50 border-l-2' : ''}`}
                style={selected?.id === inq.id ? { borderLeftColor: 'var(--brand)' } : {}}
              >
                <button
                  onClick={() => { setSelected(inq); setReply(inq.ai_reply || ''); setContext(''); setContextFiles([]); setContextUrls(''); setSelectedClient(null); setSelectedBookingId(''); }}
                  className="w-full text-left p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="font-medium text-gray-900 text-sm truncate">{inq.sender_name}</span>
                    <span className={inq.status === 'Replied' ? 'badge-replied' : 'badge-pending'} style={{ flexShrink: 0 }}>
                      {inq.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 font-medium truncate">{inq.subject}</p>
                  <p className="text-xs text-gray-400 mt-1 line-clamp-2">{inq.preview}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatTime(inq.timestamp)}</p>
                </button>
                <button
                  onClick={e => handleDeleteInquiry(e, inq.id)}
                  disabled={deletingId === inq.id}
                  className="absolute top-3 right-3 w-7 h-7 rounded flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100 disabled:opacity-50"
                  title="Verwijderen"
                >
                  {deletingId === inq.id
                    ? <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                    : <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  }
                </button>
              </div>
            ))
          )}
        </div>

        {/* Right: detail panel */}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          {!selected ? (
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className="text-sm font-medium text-gray-700">Selecteer een vraag</h3>
                <p className="text-xs text-gray-400 mt-1">Klik op een vraag links om te beginnen</p>
              </div>
            </div>
          ) : (
            <div className="p-6 max-w-2xl">
              {/* Inquiry card */}
              <div className="pro-card p-5 mb-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">{selected.sender_name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{selected.subject}</p>
                  </div>
                  <span className={selected.status === 'Replied' ? 'badge-replied' : 'badge-pending'}>
                    {selected.status}
                  </span>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700 leading-relaxed border border-gray-100">
                  {selected.full_text}
                </div>
              </div>

              {/* Context card */}
              <div className="pro-card p-5 mb-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-1">Extra context voor AI</h4>
                <p className="text-xs text-gray-400 mb-4">Optioneel — helpt de AI een beter antwoord te geven</p>
                <div className="space-y-3">
                  <div>
                    <label className="label text-xs">Klant koppelen <span className="font-normal text-gray-400">(optioneel)</span></label>
                    <ClientSearch
                      selectedClient={selectedClient}
                      onSelect={setSelectedClient}
                      onClear={() => setSelectedClient(null)}
                    />
                  </div>
                  {bookings.length > 0 && (
                    <div>
                      <label className="label text-xs">Selecteer boeking als context</label>
                      <select
                        className="input text-sm"
                        value={selectedBookingId}
                        onChange={e => handleBookingSelect(e.target.value)}
                      >
                        <option value="">— Geen boeking geselecteerd —</option>
                        {bookings.map(b => {
                          const names = Array.isArray(b.traveler_names) ? b.traveler_names.join(' & ') : (b.traveler_names || 'Onbekend');
                          const dep = b.departure_date ? new Date(b.departure_date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '?';
                          return (
                            <option key={b.id} value={b.id}>
                              {names} — {b.destination || '?'} ({dep})
                            </option>
                          );
                        })}
                      </select>
                      {selectedBookingId && (
                        <button
                          onClick={() => { setSelectedBookingId(''); setContext(''); }}
                          className="mt-1 text-xs text-red-500 hover:text-red-700"
                        >
                          Boeking wissen
                        </button>
                      )}
                    </div>
                  )}
                  <div>
                    <label className="label text-xs">Klantinformatie / Bestemmingsinfo / Boekingsdetails</label>
                    <textarea
                      className="input text-sm min-h-28 resize-y"
                      placeholder={`Typ hier extra context die de AI moet gebruiken bij het antwoorden.\n\nBijv.:\n• Klant reist naar Thailand in april\n• Geen vaccinaties vereist voor zonbestemming\n• Boekingsnummer: TH-2026-4891`}
                      value={context}
                      onChange={e => setContext(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <input ref={contextFileRef} type="file" accept=".pdf,.docx,.txt" multiple className="hidden" onChange={handleContextFile} />
                    <button onClick={() => contextFileRef.current?.click()} className="btn-secondary text-xs py-1.5">
                      Document uploaden
                    </button>
                    {contextFiles.length > 0 && contextFiles.map((f, i) => (
                      <div key={i} className="flex items-center gap-1 px-3 py-1 bg-gray-100 border border-gray-200 rounded text-xs text-gray-600">
                        {f.name}
                        <button onClick={() => setContextFiles(prev => prev.filter((_, j) => j !== i))} className="ml-1 text-gray-400 hover:text-red-500">×</button>
                      </div>
                    ))}
                  </div>
                  <div>
                    <label className="label text-xs">Relevante URL's (optioneel, één per regel)</label>
                    <textarea
                      className="input text-xs min-h-12 resize-none"
                      placeholder={"https://www.who.int/health-topics/vaccines\nhttps://www.rijksoverheid.nl/reizen"}
                      value={contextUrls}
                      onChange={e => setContextUrls(e.target.value)}
                    />
                  </div>

                  <div className="pt-2 border-t border-gray-100">
                    <button
                      onClick={generateReply} disabled={generating}
                      className="btn-primary text-sm"
                    >
                      {generating ? <span className="spinner" /> : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      )}
                      {generating ? 'Genereren...' : 'AI-antwoord genereren'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Reply area */}
              {(generating || reply) && (
                <div className="pro-card p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-800">Conceptantwoord</h4>
                    {generating && <div className="spinner spinner-blue" />}
                  </div>

                  {generating && !reply ? (
                    <div className="h-32 flex items-center justify-center">
                      <div className="text-center">
                        <div className="spinner spinner-blue mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Antwoord genereren...</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="mb-3 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                        Controleer voor verzending — voeg geen persoonlijke klantgegevens toe aan dit hulpmiddel.
                      </div>

                      {/* Preview / Edit toggle */}
                      <div className="flex gap-0 mb-3 border border-gray-200 rounded-lg overflow-hidden w-fit">
                        <button
                          onClick={() => setShowHtmlPreview(true)}
                          className={`text-xs px-3 py-1.5 font-medium transition-all ${showHtmlPreview ? 'text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                          style={showHtmlPreview ? { backgroundColor: 'var(--brand)' } : {}}
                        >
                          Voorbeeld
                        </button>
                        <button
                          onClick={() => setShowHtmlPreview(false)}
                          className={`text-xs px-3 py-1.5 font-medium border-l border-gray-200 transition-all ${!showHtmlPreview ? 'text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                          style={!showHtmlPreview ? { backgroundColor: 'var(--brand)' } : {}}
                        >
                          Bewerken
                        </button>
                      </div>

                      {showHtmlPreview ? (
                        <div
                          className="rounded-lg border border-gray-200 bg-white p-4 min-h-48 text-sm"
                          style={{ fontFamily: 'Arial, sans-serif', lineHeight: '1.6' }}
                          dangerouslySetInnerHTML={{ __html: reply }}
                        />
                      ) : (
                        <textarea
                          className="input min-h-48 font-mono text-xs leading-relaxed"
                          value={reply}
                          onChange={e => setReply(e.target.value)}
                        />
                      )}

                      {sendResult && (
                        <div className={`mt-3 px-3 py-2.5 rounded-lg text-sm ${sendResult.ok ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                          {sendResult.message}
                        </div>
                      )}

                      <div className="flex gap-2 mt-4 flex-wrap pt-3 border-t border-gray-100">
                        <button onClick={sendReply} disabled={sending} className="btn-primary text-sm">
                          {sending ? <span className="spinner" /> : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                          )}
                          {sending ? 'Versturen...' : 'Verstuur e-mail'}
                        </button>
                        <button onClick={copyReply} className="btn-secondary text-sm">
                          {copied ? 'Gekopieerd' : 'Kopieer'}
                        </button>
                        <button onClick={markReplied} className="btn-secondary text-sm">
                          Markeer als beantwoord
                        </button>
                        <button onClick={() => setReply('')} className="btn-danger text-sm">
                          Verwijderen
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>}
    </div>
  );
}
